/**
 * KAN-754: диалог ручного товара: создать или изменить. Обязательно только
 * название; пустая цена: цена по запросу. Ошибки сервера показываются у
 * своих полей. Только для владельца.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useCatalogCategoryOptions, useCatalogMutations } from '@/hooks/useCatalog';
import {
  PRODUCT_NAME_MAX,
  PRODUCT_SKU_MAX,
  PRODUCT_URL_MAX,
  emptyProductForm,
  productToForm,
  serverFieldErrors,
  validateProductForm,
  type ProductFormErrors,
  type ProductFormField,
  type ProductFormValues,
} from '@/lib/catalogForm';
import type { CatalogProductDetail } from '@/types/catalog';
import { FormField } from './FormField';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Товар для изменения; нет: создание. */
  product?: CatalogProductDetail | null;
  /** Категория нового товара по умолчанию (открытая в каталоге). */
  defaultCategoryId?: string | null;
  onSaved?: (product: CatalogProductDetail) => void;
}

type TextField = Exclude<ProductFormField, 'available' | 'categoryId'>;

const TEXT_FIELDS: Array<{ field: TextField; label: string; maxLength: number; placeholder?: string; inputMode?: 'decimal' | 'numeric' }> = [
  { field: 'name', label: 'Название *', maxLength: PRODUCT_NAME_MAX },
  { field: 'price', label: 'Цена, ₽', maxLength: 20, placeholder: 'Пусто: цена по запросу', inputMode: 'decimal' },
  { field: 'sku', label: 'Артикул', maxLength: PRODUCT_SKU_MAX },
];

const LINK_FIELDS: Array<{ field: TextField; label: string }> = [
  { field: 'url', label: 'Ссылка на товар' },
  { field: 'pictureUrl', label: 'Ссылка на картинку' },
];

const fieldId = (field: string): string => `product-form-${field.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;

export function ProductFormDialog({ open, onOpenChange, product, defaultCategoryId, onSaved }: Props): JSX.Element {
  const { createProduct, updateProduct } = useCatalogMutations();
  const categories = useCatalogCategoryOptions();
  const [values, setValues] = useState<ProductFormValues>(() => emptyProductForm());
  const [errors, setErrors] = useState<ProductFormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setValues(product ? productToForm(product) : emptyProductForm(defaultCategoryId ?? null));
    setErrors({});
    setFormError(null);
  }, [open, product, defaultCategoryId]);

  const pending = createProduct.isPending || updateProduct.isPending;
  const set = <K extends ProductFormField>(field: K, value: ProductFormValues[K]): void =>
    setValues((prev) => ({ ...prev, [field]: value }));

  const submit = async (): Promise<void> => {
    const { body, errors: clientErrors } = validateProductForm(values);
    setErrors(clientErrors);
    setFormError(null);
    if (!body) return;
    try {
      const saved = product
        ? await updateProduct.mutateAsync({ id: product.id, body })
        : await createProduct.mutateAsync(body);
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      const server = serverFieldErrors(err, 'Не удалось сохранить товар');
      setErrors(server.fields as ProductFormErrors);
      setFormError(server.form);
    }
  };

  const textInput = (field: TextField, maxLength: number, placeholder?: string, inputMode?: 'decimal' | 'numeric') => (
    <Input
      id={fieldId(field)}
      value={values[field]}
      maxLength={maxLength}
      placeholder={placeholder}
      inputMode={inputMode}
      autoComplete="off"
      disabled={pending}
      aria-invalid={errors[field] ? true : undefined}
      aria-describedby={errors[field] ? `${fieldId(field)}-error` : undefined}
      onChange={(e) => set(field, e.target.value)}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-form-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{product ? 'Товар: изменить' : 'Новый товар'}</DialogTitle>
          <DialogDescription>Товар добавляется вручную, фид его не меняет и не удаляет.</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {TEXT_FIELDS.map(({ field, label, maxLength, placeholder, inputMode }) => (
            <FormField key={field} id={fieldId(field)} label={label} error={errors[field]}>
              {textInput(field, maxLength, placeholder, inputMode)}
            </FormField>
          ))}
          <FormField id={fieldId('categoryId')} label="Категория" error={errors.categoryId}>
            <NativeSelect
              id={fieldId('categoryId')}
              options={[{ value: '', label: 'Без категории' }, ...categories]}
              value={values.categoryId}
              disabled={pending}
              onChange={(e) => set('categoryId', e.target.value)}
            />
          </FormField>
          <div className="flex items-center justify-between gap-3">
            <label htmlFor={fieldId('available')} className="text-sm font-medium">
              В наличии
            </label>
            <Switch
              id={fieldId('available')}
              aria-label="В наличии"
              checked={values.available}
              disabled={pending}
              onCheckedChange={(checked) => set('available', checked)}
            />
          </div>
          <FormField id={fieldId('stockQuantity')} label="Количество на складе" error={errors.stockQuantity}>
            {textInput('stockQuantity', 10, 'Не указано', 'numeric')}
          </FormField>
          <FormField id={fieldId('description')} label="Описание" error={errors.description}>
            <Textarea
              id={fieldId('description')}
              rows={3}
              value={values.description}
              disabled={pending}
              onChange={(e) => set('description', e.target.value)}
            />
          </FormField>
          {LINK_FIELDS.map(({ field, label }) => (
            <FormField key={field} id={fieldId(field)} label={label} error={errors[field]}>
              {textInput(field, PRODUCT_URL_MAX, 'https://...')}
            </FormField>
          ))}
          {formError && (
            <p className="text-xs text-red-700" data-testid="product-form-error">
              {formError}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
              Отмена
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
